"use client";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/inline-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost } from "@/lib/api-client";
import type { SeasonRange } from "@/lib/api-client";
import { Trash2, Plus, CalendarRange } from "lucide-react";
import { formatDate, formatPrice } from "@/components/booking/format";
import { toast } from "sonner";
import { useState } from "react";
import { useI18n } from "@/lib/i18n/provider";

export function SeasonsModal({
  productId,
  highSeasonRate,
  open,
  onOpenChange,
}: {
  productId: string | null;
  highSeasonRate: number;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const { t, locale } = useI18n();
  const [name, setName] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [busy, setBusy] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["seasons", productId],
    queryFn: () =>
      apiGet<{ seasonRanges: SeasonRange[] }>(`/api/products/${productId}/seasons`),
    enabled: !!productId && open,
  });

  const list = data?.seasonRanges ?? [];

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!productId || !start || !end) return;
    setBusy(true);
    try {
      await apiPost(`/api/products/${productId}/seasons`, {
        name: name || t("season.highSeason"),
        startDate: start,
        endDate: end,
      });
      toast.success(t("toast.seasonAdded"));
      setName("");
      setStart("");
      setEnd("");
      qc.invalidateQueries({ queryKey: ["seasons", productId] });
      qc.invalidateQueries({ queryKey: ["product", productId] });
      qc.invalidateQueries({ queryKey: ["products"] });
    } catch (err: any) {
      toast.error(err.message || "Could not add range");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    if (!productId) return;
    try {
      await apiDelete(`/api/products/${productId}/seasons/${id}`);
      toast.success(t("toast.seasonRemoved"));
      qc.invalidateQueries({ queryKey: ["seasons", productId] });
      qc.invalidateQueries({ queryKey: ["product", productId] });
      qc.invalidateQueries({ queryKey: ["products"] });
    } catch (err: any) {
      toast.error(err.message || "Could not remove");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarRange className="h-5 w-5" /> {t("season.ranges")}
          </DialogTitle>
          <DialogDescription>
            {t("season.highSeasonRateHint")}{" "}
            {highSeasonRate > 0 && (
              <span className="font-medium text-foreground">
                {t("season.highSeasonBadge", { rate: formatPrice(highSeasonRate) })}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={add} className="grid gap-3 rounded-lg border p-3">
          <div className="grid gap-1.5">
            <Label>{t("season.rangeName")}</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("season.highSeason")}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>{t("season.from")}</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} required />
            </div>
            <div className="grid gap-1.5">
              <Label>{t("season.to")}</Label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} required />
            </div>
          </div>
          <Button type="submit" disabled={busy} className="gap-2">
            <Plus className="h-4 w-4" /> {t("season.addRange")}
          </Button>
        </form>

        <div className="space-y-2">
          <h4 className="text-sm font-semibold">{t("season.current", { count: list.length })}</h4>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
          ) : list.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("season.empty")}</p>
          ) : (
            <ul className="grid gap-2">
              {list.map((s) => (
                <li
                  key={s.id}
                  className="flex items-center justify-between rounded-lg border bg-card px-3 py-2 text-sm"
                >
                  <div>
                    <div className="font-medium">{s.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {formatDate(s.startDate, locale)} — {formatDate(s.endDate, locale)}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    onClick={() => remove(s.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
